const OUI_PREFIX = "283B96";
export const UNKNOWN_DEVICE_FAMILY = "unknown";

/** Fourth MAC byte (hex) maps to product family per CM-115 serial structure. */
const BYTE4_TO_PRODUCT: Record<string, string> = {
  "00": "CoolMasterNet",
  "01": "CoolMasterNet RUS",
  "02": "CloudBox",
  "03": "CoolMasterLite",
  "04": "CoolMaster EDGE",
  "05": "CoolMaster LP",
  "10": "CoolMasterLT",
  "11": "CoolMaster",
  "12": "CoolMasterPRO",
  "13": "HBN200-PRO",
  C0: "CoolLinkNet",
  C1: "CoolLinkHub",
  C2: "DCAHLA",
  C3: "CoolLinkBridge"
};

export interface MacFormatValidationResult {
  valid: boolean;
  message: string;
  family: string;
  expectedPrefix?: string;
}

export const normalizeDeviceMac = (serial?: string) =>
  (serial || "").replace(/[^0-9A-Fa-f]/g, "").toUpperCase();

const resolveFamilyFromByte4 = (byte4: string) => {
  if (byte4.startsWith("D")) {
    return "CoolPlug";
  }

  return BYTE4_TO_PRODUCT[byte4] || UNKNOWN_DEVICE_FAMILY;
};

const formatExpectedMac = (byte4: string) =>
  byte4.startsWith("D")
    ? `${OUI_PREFIX.slice(0, 2)} ${OUI_PREFIX.slice(2, 4)} ${OUI_PREFIX.slice(4, 6)} D* ** **`
    : `${OUI_PREFIX.slice(0, 2)} ${OUI_PREFIX.slice(2, 4)} ${OUI_PREFIX.slice(4, 6)} ${byte4} ** **`;

export const getDeviceFamilyFromSerial = (serial?: string) => {
  const mac = normalizeDeviceMac(serial);

  if (!mac || mac.length !== 12 || !mac.startsWith(OUI_PREFIX)) {
    return UNKNOWN_DEVICE_FAMILY;
  }

  return resolveFamilyFromByte4(mac.substring(6, 8));
};

export const validateDeviceMacFormat = (
  serial?: string
): MacFormatValidationResult => {
  const mac = normalizeDeviceMac(serial);
  const family = getDeviceFamilyFromSerial(serial);

  if (!mac) {
    return {
      valid: false,
      message: "Missing MAC",
      family: UNKNOWN_DEVICE_FAMILY
    };
  }

  if (mac.length !== 12) {
    return {
      valid: false,
      message: "MAC must be 12 hex characters",
      family: UNKNOWN_DEVICE_FAMILY
    };
  }

  if (!mac.startsWith(OUI_PREFIX)) {
    return {
      valid: false,
      message: `Expected prefix ${OUI_PREFIX}`,
      family: UNKNOWN_DEVICE_FAMILY,
      expectedPrefix: `${OUI_PREFIX.slice(0, 2)} ${OUI_PREFIX.slice(2, 4)} ${OUI_PREFIX.slice(4, 6)} ** ** **`
    };
  }

  if (family === UNKNOWN_DEVICE_FAMILY) {
    return {
      valid: false,
      message: "Unknown product family",
      family: UNKNOWN_DEVICE_FAMILY
    };
  }

  return {
    valid: true,
    message: "Valid",
    family,
    expectedPrefix: formatExpectedMac(mac.substring(6, 8))
  };
};

export const getMacValidationLabel = (result: MacFormatValidationResult) =>
  result.valid ? "Valid" : "Invalid";
